import {
	Table,
	TableContainer,
	TableHead,
	TableRow,
	TableCell,
	TableBody,
	IconButton,
	Tooltip,
	Box,
	Typography,
	Chip,
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import Paper from "@mui/material/Paper";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import { DevicesTableProps } from "./types";
import { Link, useNavigate } from "react-router-dom";
import {
	FetchDeviceWithHistoryDto,
} from "../../../../models/Device";
import { DatesUtils } from "../../../../utils/DatesUtils";
import { isEmpty } from "lodash";
import { CabinetState } from "../../../../models/DeviceHistory";
import { getDerivedDeviceStatus } from "../../../../utils/deviceStatus";
import { DeviceStatus } from "../../../../models/Device";

export default function DevicesTable(props: DevicesTableProps) {
	const { devices, permissionaryConfiguration } = props;
	const navigate = useNavigate();

	const navigateToDeviceDetails = (deviceId: string, isEditMode = false) => {
		const route = "/devices/" + deviceId;
		navigate(route, {
			state: {
				isEditMode,
			},
		});
	};

	const getConnectivityStatus = (device: FetchDeviceWithHistoryDto) => {
		const derivedStatus = getDerivedDeviceStatus(
			device,
			permissionaryConfiguration,
		);

		if (derivedStatus === DeviceStatus.OFFLINE) {
			return (
				<Tooltip title='Fuera de linea' arrow>
					<Chip
						icon={<ErrorIcon />}
						label='Fuera de linea'
						size='small'
						sx={{
							backgroundColor: "#f1f5f9",
							color: "#64748b",
							fontWeight: 700,
							"& .MuiChip-icon": { color: "#94a3b8" },
						}}
					/>
				</Tooltip>
			);
		}

		if (derivedStatus === DeviceStatus.ONLINE) {
			return (
				<Tooltip title='En linea' arrow>
					<Chip
						icon={<CheckCircleIcon />}
						label='En linea'
						size='small'
						sx={{
							backgroundColor: "#dcfce7",
							color: "#166534",
							fontWeight: 700,
							"& .MuiChip-icon": { color: "#16a34a" },
						}}
					/>
				</Tooltip>
			);
		}

		if (derivedStatus === DeviceStatus.ERROR) {
			return (
				<Tooltip title='Error de Conexion' arrow>
					<Chip
						icon={<ErrorIcon />}
						label='Error'
						size='small'
						sx={{
							backgroundColor: "#fee2e2",
							color: "#991b1b",
							fontWeight: 700,
							"& .MuiChip-icon": { color: "#dc2626" },
						}}
					/>
				</Tooltip>
			);
		}

		return (
			<Chip
				label='Sin datos'
				size='small'
				sx={{
					backgroundColor: "#f8fafc",
					color: "#64748b",
					fontWeight: 700,
				}}
			/>
		);
	};

	return (
		<TableContainer
			component={Paper}
			elevation={0}
			sx={{
				borderRadius: 3,
				border: "1px solid rgba(15, 23, 42, 0.08)",
				overflow: "hidden",
				boxShadow: "0 10px 30px rgba(15, 23, 42, 0.08)",
			}}>
			<Table
				size='medium'
				aria-label='tabla de dispositivos'
				sx={{
					minWidth: 920,
					borderCollapse: "separate",
					borderSpacing: 0,
					"& .MuiTableCell-head": {
						backgroundColor: "#f8fafc",
						color: "#334155",
						fontSize: 12,
						fontWeight: 800,
						letterSpacing: "0.02em",
						textTransform: "uppercase",
						borderBottom: "1px solid rgba(15, 23, 42, 0.08)",
						py: 1.6,
					},
					"& .MuiTableCell-body": {
						color: "#1e293b",
						borderBottom: "1px solid rgba(15, 23, 42, 0.07)",
						py: 1.35,
					},
				}}>
				<TableHead>
					<TableRow>
						<TableCell align='center'>Device ID</TableCell>
						<TableCell align='center'>Voltaje Principal</TableCell>
						<TableCell align='center'>Estado Conectividad</TableCell>
						<TableCell align='center'>Estado de Gabinete</TableCell>
						<TableCell align='center'>Temperatura</TableCell>
						<TableCell align='center'>Ultima fecha de reporte</TableCell>
						<TableCell align='center'>Acciones</TableCell>
					</TableRow>
				</TableHead>
				<TableBody>
					{isEmpty(devices) && (
						<TableRow
							sx={{
								"&:last-child td, &:last-child th": { border: 0 },
							}}>
							<TableCell colSpan={7}>
								<Box sx={{ textAlign: "center", py: 6 }}>
									<Typography
										variant='h6'
										sx={{ color: "#0f172a", fontWeight: 800 }}>
										No hay dispositivos
									</Typography>
									<Typography variant='body2' sx={{ color: "#64748b", mt: 0.5 }}>
										Agrega un dispositivo o ajusta los filtros actuales.
									</Typography>
								</Box>
							</TableCell>
						</TableRow>
					)}
					{!isEmpty(devices) &&
						devices.map((row) => (
							<TableRow
								key={row.id}
								hover
								sx={{
									transition: "background-color 160ms ease",
									"&:hover": {
										backgroundColor: "rgba(2, 132, 199, 0.04)",
									},
									"&:last-child td, &:last-child th": { border: 0 },
								}}>
								<TableCell component='th' scope='row' align='center'>
									<Tooltip title='Ver detalles' arrow>
										<Link
											to={`/devices/${row.id}`}
											style={{
												color: "#0369a1",
												fontWeight: 800,
												textDecoration: "none",
											}}>
											{row.deviceId}
										</Link>
									</Tooltip>
								</TableCell>
								<TableCell component='th' scope='row' align='center'>
									{isEmpty(row.history)
										? "Sin datos"
										: `${row.history[0].voltageMeasurement}V`}
								</TableCell>
								<TableCell component='th' scope='row' align='center'>
									{getConnectivityStatus(row)}
								</TableCell>
								<TableCell component='th' scope='row' align='center'>
									{isEmpty(row.history) ? (
										<Chip
											label='Sin datos'
											size='small'
											sx={{
												backgroundColor: "#f8fafc",
												color: "#64748b",
												fontWeight: 700,
											}}
										/>
									) : row.history[0].cabinetState === CabinetState.OPEN ? (
										<Tooltip title='Abierto' arrow>
											<Chip
												icon={<ErrorIcon />}
												label='Abierto'
												size='small'
												sx={{
													backgroundColor: "#fee2e2",
													color: "#991b1b",
													fontWeight: 700,
													"& .MuiChip-icon": { color: "#dc2626" },
												}}
											/>
										</Tooltip>
									) : (
										<Tooltip title='Cerrado' arrow>
											<Chip
												icon={<CheckCircleIcon />}
												label='Cerrado'
												size='small'
												sx={{
													backgroundColor: "#dcfce7",
													color: "#166534",
													fontWeight: 700,
													"& .MuiChip-icon": { color: "#16a34a" },
												}}
											/>
										</Tooltip>
									)}
								</TableCell>
								<TableCell component='th' scope='row' align='center'>
									{isEmpty(row.history)
										? "Sin datos"
										: row.history[0].temperatureMeasurement.toFixed(2) +
											" " +
											row.history[0].temperatureUnits}
								</TableCell>
								<TableCell component='th' scope='row' align='center'>
									{isEmpty(row.history)
										? "Sin datos"
										: DatesUtils.convertTimestampToLocal(
												row.history[0].timestamp,
											)}
								</TableCell>
								<TableCell component='th' scope='row' align='center'>
									<Tooltip title='Ver detalles' arrow>
										<IconButton
											aria-label='Ver detalles'
											onClick={() => {
												navigateToDeviceDetails(row.id, false);
											}}
											sx={{
												color: "#0369a1",
												backgroundColor: "rgba(2, 132, 199, 0.08)",
												"&:hover": {
													backgroundColor: "rgba(2, 132, 199, 0.15)",
												},
											}}>
											<VisibilityIcon />
										</IconButton>
									</Tooltip>
								</TableCell>
							</TableRow>
						))}
				</TableBody>
			</Table>
		</TableContainer>
	);
}
